import { useNavigate } from "react-router-dom";
import { LifeBuoy, UserX, FileMinus, ArrowRight, ArrowLeft } from "lucide-react";
import yLogoDark from "@/assets/brand/y-logo-dark.png";
import { Seo } from "@/lib/useSeo";

/**
 * Help hub — reached from the pre-signup Landing footer.
 *
 *   Left box  → /support         (routes to the existing Contact form)
 *   Right box → /help/delete     (Delete Account flow: auth → warn → confirm → done)
 *
 * Kept on the same dark BrontoY canvas as the Landing page so the visual
 * language stays 1:1 across the pre-signup surface.
 */
export default function Help() {
  const nav = useNavigate();
  return (
    <div
      className="min-h-dvh w-full bg-black text-white flex flex-col"
      data-testid="help-screen"
      style={{
        fontFamily:
          "'Plus Jakarta Sans','TwitterChirp','Segoe UI',Helvetica,Arial,sans-serif",
      }}
    >
      <Seo
        title="Help — BrontoY"
        description="Get in touch with the BrontoY team or request permanent deletion of your account."
        canonicalPath="/help"
      />

      {/* Ambient glow — mirrors the Landing hero mood */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 overflow-hidden"
        style={{ position: "fixed", zIndex: 0 }}
      >
        <div
          style={{
            position: "absolute",
            top: "-15%",
            left: "-10%",
            width: "700px",
            height: "700px",
            background:
              "radial-gradient(closest-side, rgba(168,85,247,0.28), transparent)",
            filter: "blur(40px)",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "-25%",
            right: "-15%",
            width: "700px",
            height: "700px",
            background:
              "radial-gradient(closest-side, rgba(236,72,153,0.22), transparent)",
            filter: "blur(40px)",
          }}
        />
      </div>

      {/* Top bar */}
      <header className="relative z-10 h-14 px-4 md:px-8 flex items-center justify-between border-b border-white/10">
        <button
          type="button"
          onClick={() => (window.history.length > 1 ? nav(-1) : nav("/"))}
          className="inline-flex items-center gap-1.5 text-white/70 hover:text-white text-[13.5px] font-medium"
          data-testid="help-back"
        >
          <ArrowLeft size={16} strokeWidth={2.4} /> Back
        </button>
        <button
          type="button"
          onClick={() => nav("/")}
          className="inline-flex items-center gap-2"
          data-testid="help-y-logo"
        >
          <img
            src={yLogoDark}
            alt="BrontoY"
            className="h-7 w-auto"
            draggable={false}
          />
        </button>
        <span className="w-10" />
      </header>

      <main className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 py-14 max-w-[1180px] w-full mx-auto">
        <div className="w-full text-center mb-10 md:mb-14">
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-white/40 mb-3">
            BrontoY Help
          </p>
          <h1
            className="text-[36px] leading-[1.05] md:text-[54px] lg:text-[60px] font-extrabold tracking-tight"
            data-testid="help-title"
          >
            How can we help?
          </h1>
          <p className="mt-4 text-white/60 text-[15px] md:text-[16.5px] max-w-[560px] mx-auto">
            Reach the BrontoY team for support — or permanently delete your account below.
          </p>
        </div>

        <div className="grid gap-5 md:gap-6 md:grid-cols-2 lg:grid-cols-3 w-full max-w-[1080px]">
          {/* Get in Touch — routes to existing Support/Contact page */}
          <HelpCard
            testId="help-card-contact"
            onClick={() => nav("/support")}
            Icon={LifeBuoy}
            iconColor="from-purple-500 to-pink-500"
            title="Get in Touch"
            body="Bugs, account questions, media requests, or feedback — reach the BrontoY team."
            cta="Open contact form"
          />

          {/* Delete Account — routes to /help/delete auth options */}
          <HelpCard
            testId="help-card-delete"
            onClick={() => nav("/help/delete")}
            Icon={UserX}
            iconColor="from-rose-500 to-red-600"
            title="Delete Account"
            body="Permanently erase your BrontoY account, posts and data. Sign in first to confirm."
            cta="Start deletion"
            danger
          />

          {/* Delete Account Data — routes to /help/delete-account-data guide */}
          <HelpCard
            testId="help-card-delete-data"
            onClick={() => nav("/help/delete-account-data")}
            Icon={FileMinus}
            iconColor="from-amber-300 to-yellow-500"
            title="Delete Account Data"
            body="Remove specific posts, comments, and other data from your account — without deleting the whole account."
            cta="See how"
            accent="yellow"
          />
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 w-full pt-8 pb-8 flex flex-col items-center gap-2 border-t border-white/10 text-center px-6">
        <p className="text-white/50 text-[12.5px]">
          BrontoY <span className="text-white/30">·</span> By Bronto Intelligence
        </p>
      </footer>
    </div>
  );
}

function HelpCard({ testId, onClick, Icon, iconColor, title, body, cta, danger, accent }) {
  const isYellow = accent === "yellow";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group relative overflow-hidden rounded-3xl border text-left p-6 md:p-8 transition-all duration-300 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40 ${
        danger
          ? "border-red-500/25 bg-gradient-to-br from-red-950/60 via-black to-black hover:border-red-500/60"
          : isYellow
          ? "border-yellow-400/25 bg-gradient-to-br from-amber-950/50 via-black to-black hover:border-yellow-400/60"
          : "border-white/10 bg-gradient-to-br from-purple-950/40 via-black to-black hover:border-white/30"
      }`}
      data-testid={testId}
    >
      {/* Sheen sweep on hover */}
      <span
        aria-hidden
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{
          background:
            "linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.05) 50%, transparent 70%)",
        }}
      />

      <div
        className={`inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br ${iconColor} mb-5 ${
          isYellow
            ? "shadow-[0_10px_30px_-10px_rgba(250,204,21,0.55)]"
            : "shadow-[0_10px_30px_-10px_rgba(236,72,153,0.6)]"
        }`}
      >
        <Icon className="text-white" size={22} strokeWidth={2.3} />
      </div>

      <h2 className="text-white text-[22px] md:text-[24px] font-extrabold tracking-tight mb-2">
        {title}
      </h2>
      <p className="text-white/60 text-[14px] md:text-[14.5px] leading-relaxed max-w-[360px] mb-6">
        {body}
      </p>

      <span
        className={`inline-flex items-center gap-1.5 text-[13.5px] font-semibold ${
          danger
            ? "text-red-300 group-hover:text-red-200"
            : isYellow
            ? "text-yellow-300 group-hover:text-yellow-200"
            : "text-white/85 group-hover:text-white"
        }`}
      >
        {cta}
        <ArrowRight
          size={15}
          strokeWidth={2.6}
          className="transition-transform duration-300 group-hover:translate-x-0.5"
        />
      </span>
    </button>
  );
}
